<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import HobbiiCoinImg from '@/images/hobbii-coin.png';
import IconDiscountCode from '@/shared/components/UI/Icons/IconDiscountCode.vue';
import SkeletonLoader from '@/shared/components/UI/SkeletonLoader.vue';
import { useCartPoints } from '@/shared/composables/cart/useCartPoints';
import { useCartShipping } from '@/shared/composables/cart/useCartShipping';
import { useTranslations } from '@/shared/composables/useTranslations';
import { useCartStore } from '@/shared/pinia/modules/cart';
import { getCountryCode } from '@/shared/utils/country';
import {
  formattedPriceWithDecimals as formattedPrice,
  parseCompareAtPrice
} from '@/shared/utils/shopify/money-formatter';

type Props = {
  shippingThreshold?: number;
  lowestShippingPrice?: number;
  showSalesTaxMessage?: boolean;
  display?: 'mini' | 'main';
};

const props = withDefaults(defineProps<Props>(), {
  shippingThreshold: 0,
  lowestShippingPrice: 0,
  showSalesTaxMessage: false,
  display: 'mini'
});

const cartStore = useCartStore();
const { loadingCart: isLoading } = storeToRefs(cartStore);
const { t } = useTranslations();
const {
  shippingSubtotal,
  hasPhysicalProducts,
  cartLevelDiscounts,
  totalCartLevelDiscounts
} = useCartShipping();
const { formattedPoints, points } = useCartPoints();

// ─── Shipping ─────────────────────────────────────────────────────────────────

const calculatedShippingCost = computed(() => {
  if (!hasPhysicalProducts.value) return 0;

  const effectiveSubtotal =
    shippingSubtotal.value - totalCartLevelDiscounts.value;

  if (
    props.shippingThreshold > 0 &&
    effectiveSubtotal >= props.shippingThreshold
  ) {
    return 0;
  }
  return props.lowestShippingPrice || 0;
});

const shippingCostFormatted = computed(() =>
  calculatedShippingCost.value > 0
    ? formattedPrice(calculatedShippingCost.value)
    : t('shipping.text_free_shipping')
);

// ─── Subtotals ────────────────────────────────────────────────────────────────

const originalCartSubtotal = computed(() => {
  if (!cartStore.cart?.items) return 0;

  return cartStore.cart.items.reduce((total, item) => {
    const originalPrice = item.original_price || 0;
    const originalLinePrice = item.original_line_price || 0;

    const compareAtPrice = item.variant?.compare_at_price
      ? parseCompareAtPrice(item.variant.compare_at_price)
      : 0;

    const maxOriginalOrCompareAtLinePrice =
      compareAtPrice > originalPrice
        ? compareAtPrice * (item.quantity || 1)
        : originalLinePrice;

    return total + maxOriginalOrCompareAtLinePrice;
  }, 0);
});

const itemsSubtotalPrice = computed(
  () => cartStore.cart?.items_subtotal_price ?? 0
);

const totalSaved = computed(
  () => originalCartSubtotal.value - itemsSubtotalPrice.value
);

const formattedOriginalSubtotal = computed(() =>
  formattedPrice(originalCartSubtotal.value)
);

const formattedTotalSaved = computed(() => formattedPrice(totalSaved.value));

// ─── Total (main display only) ────────────────────────────────────────────────

const finalCartTotal = computed(
  () => (cartStore.cart?.total_price || 0) + calculatedShippingCost.value
);

const formattedFinalTotal = computed(() =>
  formattedPrice(finalCartTotal.value)
);

// ─── Sales tax (main display only) ───────────────────────────────────────────

const salesTaxMessage = computed(() => {
  if (!props.showSalesTaxMessage) return null;

  const salesTaxSettings = window.Hobbii?.salesTaxSettings;
  if (!salesTaxSettings) return null;

  const message =
    getCountryCode() === 'CA'
      ? salesTaxSettings.priceDisclaimerForCA
      : salesTaxSettings.priceDisclaimerForUS;

  return message && message.trim() !== '' ? message : null;
});
</script>

<template>
  <div
    class="js-cart-lines-summary text-sm"
    :class="
      display === 'mini'
        ? 'mx-4 !border-t border-b py-4 md:mx-8 md:border-b-0'
        : 'flex justify-end border-b py-4'
    "
  >
    <!-- Skeleton shown while cart data is loading -->
    <template v-if="isLoading || !cartStore.cart">
      <div
        class="grid w-full"
        :class="
          display === 'mini'
            ? 'grid-cols-[1fr,auto] gap-2.5'
            : 'grid-cols-[3fr,1fr] gap-2 md:max-w-sm'
        "
        aria-hidden="true"
      >
        <SkeletonLoader class="w-32" />
        <SkeletonLoader class="w-16 justify-self-end" />

        <SkeletonLoader class="w-24" />
        <SkeletonLoader class="w-12 justify-self-end" />

        <SkeletonLoader class="w-20" />
        <SkeletonLoader class="mb-2 w-14 justify-self-end" />

        <SkeletonLoader type="button" class="w-40" />
        <SkeletonLoader type="button" class="w-20 justify-self-end" />
      </div>
    </template>

    <!-- Actual content once cart is loaded -->
    <template v-else>
      <div
        class="grid w-full"
        :class="
          display === 'mini'
            ? 'grid-cols-[1fr,auto] items-end gap-2.5'
            : 'grid-cols-[3fr,1fr] gap-2 md:max-w-sm'
        "
      >
        <!-- Subtotal -->
        <span>{{ t('cart.original_subtotal') }}</span>
        <span class="text-right">{{ formattedOriginalSubtotal }}</span>

        <!-- Total saved -->
        <template v-if="totalSaved > 0">
          <span>{{ t('cart.total_saved') }}</span>
          <span
            class="text-right"
            :class="display === 'mini' ? 'text-pink' : 'text-secondary'"
            >-{{ formattedTotalSaved }}</span
          >
        </template>

        <!-- Cart-level discounts -->
        <template v-for="(discount, index) in cartLevelDiscounts" :key="index">
          <span
            class="inline-flex items-center gap-0.5 justify-self-start rounded-md bg-gray-200 px-1 py-0.5 text-xs text-gray-500"
          >
            <IconDiscountCode class="shrink-0 text-xs" />
            {{ discount.title }}
          </span>
          <span
            class="text-right"
            :class="display === 'mini' ? 'text-pink' : 'text-secondary'"
            >-{{ formattedPrice(discount.amount) }}</span
          >
        </template>

        <!-- Shipping -->
        <template v-if="hasPhysicalProducts">
          <span>{{ t('shipping.text_shipping') }}</span>
          <span class="text-right">{{ shippingCostFormatted }}</span>
        </template>

        <!-- Points (mini only) -->
        <template v-if="display === 'mini' && points > 0">
          <span>{{ t('cart.you_earn') }}</span>
          <span class="flex items-center gap-1">
            <img
              :src="HobbiiCoinImg"
              class="mt-0.5 w-4 md:m-0"
              width="20"
              height="20"
              loading="lazy"
              alt=""
            />
            <span>{{ formattedPoints }} {{ t('cart.points') }}</span>
          </span>
        </template>

        <!-- Total + tax (main only) -->
        <template v-if="display === 'main'">
          <span>
            <span class="font-bold">{{ t('cart.total') }}</span>
            <br />
            <span
              v-if="salesTaxMessage"
              class="text-xs leading-tight text-gray-400"
            >
              {{ salesTaxMessage }}
            </span>
            <span v-else class="text-xs leading-tight text-gray-400">
              {{ t('cart.total_disclaimer') }}
            </span>
          </span>
          <span class="text-right text-lg font-bold">{{
            formattedFinalTotal
          }}</span>
        </template>
      </div>
    </template>
  </div>
</template>
